"use client";

// ============================================================
// CLUBHUB – Was man mit einer Gruppe tut (Menue an Zeile und Kopf)
// ============================================================
// Archivieren loescht nichts: Mitglieder und Historie bleiben, die
// Gruppe verschwindet nur aus Auswahllisten, Verteilern und dem
// Kalender. Darum fragt die Rueckfrage nicht „wirklich loeschen?“,
// sondern sagt, was passiert — und wie man zurueckkommt.
// ============================================================
import { useRouter } from "next/navigation";
import { Archive, ArchiveRestore, Ellipsis, FolderTree, Mail, Pencil, SquareArrowOutUpRight } from "lucide-react";
import { DropdownItem, DropdownMenu, DropdownSeparator, confirmDialog, toast } from "@/components/ui";
import { istFehler } from "@/lib/action-result";
import type { GruppeView } from "@/lib/gruppen/views";
import { nachfahren } from "@/lib/gruppen/logik";
import { cn } from "@/lib/utils";
import { useGruppenBestand } from "./gruppen-bestand";

const untergruppenText = (n: number) => (n === 1 ? "eine Untergruppe" : `${n} Untergruppen`);

/** Archivieren und Wiederherstellen — mit Rueckfrage und Meldung. */
export function useGruppeArchiv() {
  const { gruppen, gruppeArchivieren, gruppeWiederherstellen } = useGruppenBestand();

  async function archivieren(g: GruppeView) {
    const unter = nachfahren(gruppen, g.id).filter((id) => !gruppen.find((x) => x.id === id)?.isArchived).length;
    const saetze = [
      unter === 1 ? "Die Untergruppe wird mit archiviert." : unter > 1 ? `Die ${unter} Untergruppen werden mit archiviert.` : null,
      "Mitglieder und Historie bleiben erhalten; die Gruppe verschwindet aus Auswahllisten, Verteilern und dem Kalender.",
      g.emailAlias ? `${g.emailAlias} nimmt danach keine Mails mehr an.` : null,
      "Wiederherstellen geht jederzeit unter „Archiv“.",
    ];
    const ja = await confirmDialog({
      title: `„${g.name}“ archivieren?`,
      description: saetze.filter(Boolean).join(" "),
      confirmLabel: "Archivieren",
    });
    if (!ja) return false;
    const r = await gruppeArchivieren(g.id);
    if (istFehler(r)) {
      toast.error(r.error);
      return false;
    }
    const n = r.anzahl;
    toast.success(n > 1 ? `„${g.name}“ und ${untergruppenText(n - 1)} archiviert` : `„${g.name}“ archiviert`);
    return true;
  }

  async function wiederherstellen(g: GruppeView) {
    const r = await gruppeWiederherstellen(g.id);
    if (istFehler(r)) {
      toast.error(r.error);
      return;
    }
    const n = r.anzahl;
    toast.success(n > 1 ? `„${g.name}“ und ${untergruppenText(n - 1)} wiederhergestellt` : `„${g.name}“ wiederhergestellt`);
  }

  return { archivieren, wiederherstellen };
}

export function GruppenMenue({
  gruppe,
  onBearbeiten,
  onUntergruppe,
  mitOeffnen = false,
  className,
}: {
  gruppe: GruppeView;
  onBearbeiten: () => void;
  onUntergruppe: () => void;
  /** In Listen: „Öffnen“ als erster Eintrag (die Zeile oeffnet ohnehin). */
  mitOeffnen?: boolean;
  className?: string;
}) {
  const { rechte } = useGruppenBestand();
  const { archivieren, wiederherstellen } = useGruppeArchiv();
  const router = useRouter();

  return (
    <DropdownMenu
      trigger={
        <button
          type="button"
          aria-label={`Aktionen für ${gruppe.name}`}
          title="Aktionen"
          className={cn(
            "text-text-tertiary hover:bg-bg-tertiary hover:text-text-primary duration-fast grid h-8 w-8 place-items-center rounded-lg transition-colors",
            className,
          )}
        >
          <Ellipsis className="h-4 w-4" aria-hidden />
        </button>
      }
    >
      {mitOeffnen && (
        <DropdownItem onClick={() => router.push(`/gruppen/${gruppe.id}`)}>
          <SquareArrowOutUpRight className="h-4 w-4" aria-hidden />
          Öffnen
        </DropdownItem>
      )}
      {!gruppe.isArchived && (
        <DropdownItem onClick={() => router.push(`/nachrichten/neu?gruppe=${gruppe.id}`)}>
          <Mail className="h-4 w-4" aria-hidden />
          Nachricht an die Gruppe
        </DropdownItem>
      )}
      {rechte.schreiben && (
        <>
          <DropdownSeparator />
          <DropdownItem onClick={onBearbeiten}>
            <Pencil className="h-4 w-4" aria-hidden />
            Bearbeiten
          </DropdownItem>
          {!gruppe.isArchived && (
            <DropdownItem onClick={onUntergruppe}>
              <FolderTree className="h-4 w-4" aria-hidden />
              Untergruppe anlegen
            </DropdownItem>
          )}
          <DropdownSeparator />
          {gruppe.isArchived ? (
            <DropdownItem onClick={() => void wiederherstellen(gruppe)}>
              <ArchiveRestore className="h-4 w-4" aria-hidden />
              Wiederherstellen
            </DropdownItem>
          ) : (
            <DropdownItem variant="danger" onClick={() => void archivieren(gruppe)}>
              <Archive className="h-4 w-4" aria-hidden />
              Archivieren
            </DropdownItem>
          )}
        </>
      )}
    </DropdownMenu>
  );
}
